import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { TanTable } from '../../components/TanTable';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/**
 * VIN Override Maintenance (Fleet Management → VIN Overrides).
 *
 * Lists the active engine-swap acknowledgements from vin_override (the rows that
 * suppress a known V1↔Samsara VIN mismatch and resolve the truck on the map/ETA).
 * Edit fixes a wrong pinned pair / reason; Remove soft-deletes it (active=0).
 * New overrides are created via the "Acknowledge" action on Driver Data Issues.
 */

interface VinOverrideRow {
  unitnumber: string;
  v1_vin: string;
  samsara_vin: string;
  reason: string | null;
  created_by: string | null;
  created_at: string | null;
}

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const VinOverrides: React.FC = () => {
  const [rows, setRows] = useState<VinOverrideRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  // Edit modal state
  const [editRow, setEditRow] = useState<VinOverrideRow | null>(null);
  const [editV1, setEditV1] = useState('');
  const [editSamsara, setEditSamsara] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const {
    panelRef: editPanelRef,
    panelStyle: editPanelStyle,
    dragHandleProps: editDragHandleProps,
  } = useModalDrag(editRow !== null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/fleet-management/vin-overrides');
      setRows(res.data.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load VIN overrides');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = useCallback((row: VinOverrideRow) => {
    setEditError(null);
    setEditRow(row);
    setEditV1(row.v1_vin || '');
    setEditSamsara(row.samsara_vin || '');
    setEditReason(row.reason || '');
  }, []);

  const closeEdit = () => {
    if (saving) return;
    setEditRow(null);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!editV1.trim() || !editSamsara.trim()) {
      setEditError('Both V1 VIN and Samsara VIN are required.');
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      const res = await api.post('/fleet-management/vin-override', {
        unitnumber: editRow.unitnumber,
        v1_vin: editV1.trim(),
        samsara_vin: editSamsara.trim(),
        reason: editReason.trim() || null,
      });
      if (res.data?.success === false) throw new Error(res.data?.error || 'Save failed');
      setToast({ kind: 'success', msg: `Override for unit ${editRow.unitnumber} saved.` });
      setEditRow(null);
      load();
    } catch (e: any) {
      setEditError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onRemove = useCallback(async (row: VinOverrideRow) => {
    if (!window.confirm(
      `Remove the VIN override for unit ${row.unitnumber}?\n\n`
      + `The unit will flag as a VIN Mismatch again on the Driver Data Issues page.`,
    )) return;
    setRemoving((prev) => new Set(prev).add(row.unitnumber));
    setToast(null);
    try {
      const res = await api.delete(`/fleet-management/vin-override/${encodeURIComponent(row.unitnumber)}`);
      if (res.data?.success === false) throw new Error(res.data?.error || 'Remove failed');
      setToast({ kind: 'success', msg: `Override for unit ${row.unitnumber} removed.` });
      setRows((prev) => prev.filter((r) => r.unitnumber !== row.unitnumber));
    } catch (e: any) {
      setToast({ kind: 'error', msg: `Unit ${row.unitnumber}: ${e?.response?.data?.detail || e?.message || 'Remove failed'}` });
    } finally {
      setRemoving((prev) => {
        const next = new Set(prev);
        next.delete(row.unitnumber);
        return next;
      });
    }
  }, []);

  const dash = (v: string | null) => v || <span className="text-gray-400">—</span>;

  const columns = useMemo<ColumnDef<VinOverrideRow>[]>(() => [
    { accessorKey: 'unitnumber', header: 'Unit', size: 90 },
    { accessorKey: 'v1_vin', header: 'V1 VIN', cell: (i) => dash(i.getValue() as string | null), size: 180 },
    { accessorKey: 'samsara_vin', header: 'Samsara VIN', cell: (i) => dash(i.getValue() as string | null), size: 180 },
    { accessorKey: 'reason', header: 'Reason', cell: (i) => dash(i.getValue() as string | null), size: 240 },
    { accessorKey: 'created_by', header: 'Created By', cell: (i) => dash(i.getValue() as string | null), size: 130 },
    {
      id: 'created_at', header: 'Created',
      accessorFn: (r) => r.created_at ?? '',
      cell: (i) => formatDateTime(i.row.original.created_at),
      size: 160,
    },
    {
      id: 'actions', header: '', enableSorting: false, enableColumnFilter: false,
      cell: (i) => {
        const row = i.row.original;
        const busy = removing.has(row.unitnumber);
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openEdit(row)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded transition-colors"
              title="Edit override"
            >
              <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              onClick={() => onRemove(row)}
              disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 rounded transition-colors"
              title="Remove override"
            >
              <TrashIcon className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        );
      },
      size: 170,
    },
  ], [openEdit, onRemove, removing]);

  return (
    <div className="p-4 pb-24">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-heading">VIN Overrides</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Acknowledged engine-swap VIN mismatches. Editing fixes a wrong pinned VIN or reason;
            removing re-flags the unit on Driver Data Issues. New overrides are created via the
            Acknowledge action there.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-md transition-colors flex-shrink-0"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      {toast && (
        <div className={`mb-3 p-3 rounded text-sm ${toast.kind === 'success'
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'}`}>
          {toast.msg}
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="vin-overrides-maint"
        cardTitle="VIN Overrides"
        searchPlaceholder="Search overrides…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        disableVirtualization={true}
        columnFilterTypes={{
          unitnumber: 'text', v1_vin: 'text', samsara_vin: 'text',
          reason: 'text', created_by: 'text', created_at: 'text',
        }}
        defaultSorting={[{ id: 'unitnumber', desc: false }]}
      />

      {/* Edit modal */}
      {editRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4" onClick={closeEdit}>
          <div
            ref={editPanelRef}
            style={editPanelStyle}
            className="w-full max-w-md rounded-lg bg-panel shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={`Edit VIN Override — Unit ${editRow.unitnumber}`}
              onClose={saving ? undefined : closeEdit}
              dragHandleProps={editDragHandleProps}
            />
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-muted">
                Correct the pinned VINs or reason. The unit stays suppressed only while both sides
                still report these exact VINs.
              </p>
              <div>
                <label htmlFor="edit-v1" className="block text-xs font-medium text-label mb-1">V1 VIN</label>
                <input
                  id="edit-v1" value={editV1} onChange={(e) => setEditV1(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-body px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="edit-samsara" className="block text-xs font-medium text-label mb-1">Samsara VIN</label>
                <input
                  id="edit-samsara" value={editSamsara} onChange={(e) => setEditSamsara(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-body px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="edit-reason" className="block text-xs font-medium text-label mb-1">Reason (optional)</label>
                <textarea
                  id="edit-reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} rows={3}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-body px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {editError && <p className="text-sm text-red-600 dark:text-red-400">{editError}</p>}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={closeEdit} disabled={saving}
                className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit} disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VinOverrides;
